import { createSlice } from "@reduxjs/toolkit";
import { initialCollectionState } from "./collection.state";
import { bumpFollowersCount } from "./collection.utils";

import {
  getCollections,
  getCollection,
  getCollectionFormData,
  createCollection,
  updateCollection,
  deleteCollection,
  getFollowedCollections,
  getCollectionsSummary,
  addItemToCollection,
  removeItemFromCollection,
  toggleFollowCollection,
} from "./collection.thunk";

export const collectionSlice = createSlice({
  name: "collection",
  initialState: initialCollectionState,
  reducers: {
    resetCollections: (state) => {
      state.data = null;
      state.status = "idle";
      state.error = null;
    },

    resetSelectedCollection: (state) => {
      state.selectedCollection = null;
      state.selectedCollectionStatus = "idle";
      state.selectedCollectionError = null;
      state.selectedCollectionItems = [];
      state.selectedCollectionPagination = null;
    },

    resetFormData: (state) => {
      state.formData = null;
      state.formDataStatus = "idle";
      state.formDataError = null;
    },
  },

  extraReducers: (builder) => {
    // ===================== GET COLLECTIONS =====================
    builder
      .addCase(getCollections.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getCollections.fulfilled, (state, action) => {
        state.status = "success";
        state.data = action.payload.data.items;
        state.modalContent = action.payload.data.modal_content;

        if (!state.summaryTabList) {
          state.summaryTabList = action.payload.data.tab_list;
        }
      })
      .addCase(getCollections.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload ?? "Koleksiyonlar yüklenemedi.";
      });

    // ===================== GET SINGLE COLLECTION =====================
    builder
      .addCase(getCollection.pending, (state, action) => {
        state.selectedCollectionStatus = "loading";
        state.selectedCollectionError = null;

        if (!action.meta.arg.page || action.meta.arg.page === 1) {
          state.selectedCollectionItems = [];
          state.selectedCollectionPagination = null;
        }
      })
      .addCase(getCollection.fulfilled, (state, action) => {
        state.selectedCollectionStatus = "success";
        state.selectedCollection = action.payload.data.collection;
        state.selectedCollectionPagination = action.payload.data.pagination;

        const isFirstPage = action.payload.data.pagination.current_page === 1;

        if (isFirstPage) {
          state.selectedCollectionItems = action.payload.data.items;
        } else {
          state.selectedCollectionItems.push(...action.payload.data.items);
        }
      })
      .addCase(getCollection.rejected, (state, action) => {
        state.selectedCollectionStatus = "error";
        state.selectedCollectionError =
          action.payload ?? "Koleksiyon yüklenemedi.";
      });

    // ===================== FORM DATA =====================
    builder
      .addCase(getCollectionFormData.pending, (state) => {
        state.formDataStatus = "loading";
        state.formDataError = null;
      })
      .addCase(getCollectionFormData.fulfilled, (state, action) => {
        state.formDataStatus = "success";
        state.formData = action.payload.data;
      })
      .addCase(getCollectionFormData.rejected, (state, action) => {
        state.formDataStatus = "error";
        state.formDataError = action.payload ?? "Form verileri yüklenemedi.";
      });

    // ===================== CREATE =====================
    builder
      .addCase(createCollection.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createCollection.fulfilled, (state, action) => {
        state.isCreating = false;

        if (!state.data) state.data = [];

        state.data.unshift(action.payload.data);
        state.selectedCollection = action.payload.data;
      })
      .addCase(createCollection.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? "Koleksiyon oluşturulamadı.";
      });

    // ===================== UPDATE =====================
    builder
      .addCase(updateCollection.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateCollection.fulfilled, (state, action) => {
        state.isUpdating = false;

        if (state.data) {
          const i = state.data.findIndex(
            (c) => c.id === action.payload.data.id,
          );
          if (i !== -1) state.data[i] = action.payload.data;
        }

        if (state.selectedCollection?.id === action.payload.data.id) {
          state.selectedCollection = action.payload.data;
        }
      })
      .addCase(updateCollection.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? "Koleksiyon güncellenemedi.";
      });

    // ===================== DELETE =====================
    builder
      .addCase(deleteCollection.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteCollection.fulfilled, (state, action) => {
        state.isDeleting = false;

        if (state.data) {
          state.data = state.data.filter((c) => c.id !== action.meta.arg);
        }

        if (state.selectedCollection?.id === action.meta.arg) {
          state.selectedCollection = null;
        }
      })
      .addCase(deleteCollection.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? "Koleksiyon silinemedi.";
      });

    // ===================== FOLLOWED =====================
    builder
      .addCase(getFollowedCollections.pending, (state) => {
        state.followedCollectionsStatus = "loading";
      })
      .addCase(getFollowedCollections.fulfilled, (state, action) => {
        state.followedCollectionsStatus = "success";
        state.followedCollections = action.payload.data.items;
      })
      .addCase(getFollowedCollections.rejected, (state, action) => {
        state.followedCollectionsStatus = "error";
        state.followedCollectionsError =
          action.payload ?? "Takip edilenler yüklenemedi.";
      });

    // ===================== SUMMARY =====================
    builder
      .addCase(getCollectionsSummary.pending, (state) => {
        state.summaryStatus = "loading";
      })
      .addCase(getCollectionsSummary.fulfilled, (state, action) => {
        state.summaryStatus = "success";
        state.summaryTabList = action.payload.data.tab_list;
      })
      .addCase(getCollectionsSummary.rejected, (state, action) => {
        state.summaryStatus = "error";
        state.summaryError = action.payload ?? "Özet yüklenemedi.";
      });

    // ===================== ADD ITEM =====================
    builder
      .addCase(addItemToCollection.pending, (state) => {
        state.isAddingItem = true;
      })
      .addCase(addItemToCollection.fulfilled, (state, action) => {
        state.isAddingItem = false;

        if (state.selectedCollection && action.payload.status && Array.isArray(action.payload.data)) {
          state.selectedCollection.items.unshift(...action.payload.data);
        }
      })
      .addCase(addItemToCollection.rejected, (state) => {
        state.isAddingItem = false;
      });

    // ===================== REMOVE ITEM =====================
    builder
      .addCase(removeItemFromCollection.pending, (state) => {
        state.isRemovingItem = true;
      })
      .addCase(removeItemFromCollection.fulfilled, (state, action) => {
        state.isRemovingItem = false;

        if (state.selectedCollection) {
          const removedIds = action.meta.arg.data.itemIds;

          state.selectedCollection.items =
            state.selectedCollection.items.filter(
              (item) =>
                !removedIds.includes(item.platform_product_variant_id) &&
                !removedIds.includes(item.id),
            );
        }
      })
      .addCase(removeItemFromCollection.rejected, (state) => {
        state.isRemovingItem = false;
      });

    // ===================== TOGGLE FOLLOW =====================
    builder
      .addCase(toggleFollowCollection.pending, (state) => {
        state.isTogglingFollow = true;
      })
      .addCase(toggleFollowCollection.fulfilled, (state, action) => {
        state.isTogglingFollow = false;

        const isFollowed = action.payload.data.is_followed;
        const id = action.meta.arg;
        // Toggle her zaman durumu tersine çevirir; API'nin döndürdüğü yeni
        // is_followed, takipçi sayısının hangi yöne gideceğini de belirler.
        const delta = isFollowed ? 1 : -1;

        if (state.selectedCollection) {
          state.selectedCollection.is_followed = isFollowed;
          state.selectedCollection.button_list =
            state.selectedCollection.button_list.map((btn) =>
              btn.action === "follow" || btn.action === "unfollow"
                ? { ...btn, action: isFollowed ? "unfollow" : "follow" }
                : btn,
            );
          const bumped = bumpFollowersCount(
            state.selectedCollection.followers_count,
            state.selectedCollection.followers_count_text,
            delta,
          );
          state.selectedCollection.followers_count = bumped.count;
          state.selectedCollection.followers_count_text = bumped.text;
        }

        if (state.data) {
          const col = state.data.find((c) => c.id === id);
          if (col) {
            col.is_followed = isFollowed;
            col.button_list = col.button_list.map((btn) =>
              btn.action === "follow" || btn.action === "unfollow"
                ? { ...btn, action: isFollowed ? "unfollow" : "follow" }
                : btn,
            );
            const bumped = bumpFollowersCount(
              col.followers_count,
              col.followers_count_text,
              delta,
            );
            col.followers_count = bumped.count;
            col.followers_count_text = bumped.text;
          }
        }

        if (state.followedCollections) {
          const followed = state.followedCollections.find((c) => c.id === id);
          if (followed) {
            followed.is_followed = isFollowed;
            const bumped = bumpFollowersCount(
              followed.followers_count,
              followed.followers_count_text,
              delta,
            );
            followed.followers_count = bumped.count;
            followed.followers_count_text = bumped.text;
          }
        }
      })
      .addCase(toggleFollowCollection.rejected, (state) => {
        state.isTogglingFollow = false;
      });
  },
});

export const { resetCollections, resetSelectedCollection, resetFormData } =
  collectionSlice.actions;

export default collectionSlice.reducer;
